import React, { useCallback, useEffect, useRef, useState } from 'react';
import EventIcon, { iconOf, stripIcon } from './EventIcon';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { useAlertScheduler } from '../hooks/useAlertScheduler';
import { loopAlertSound, getAlarmSettings } from '../utils/sounds';
import { announceReminderA11y } from '../utils/reminderA11y';
import { todayEventsCache, onEventsChanged } from '../utils/viewCache';

// Keep the same language → speech-locale mapping used by TodayView so the
// spoken reminder matches the user's language.
function langToSpeechLang(lang) {
  const map = {
    it: 'it-IT', en: 'en-US', 'en-US': 'en-US', 'en-GB': 'en-GB',
    es: 'es-ES', 'es-ES': 'es-ES', 'es-AR': 'es-AR', 'es-MX': 'es-MX', 'es-CO': 'es-CO',
    de: 'de-DE', fr: 'fr-FR', nl: 'nl-NL',
    pt: 'pt-PT', 'pt-PT': 'pt-PT', 'pt-BR': 'pt-BR',
    ru: 'ru-RU', pl: 'pl-PL', ro: 'ro-RO', zh: 'zh-CN', ja: 'ja-JP', hi: 'hi-IN', ko: 'ko-KR', ar: 'ar-SA',
  };
  return map[lang] || 'it-IT';
}

// The safety net, not the main mechanism.
//
// This used to run every three minutes, and measurement on a real phone showed
// what that cost: /events/week called twenty-one times in a day, 1.27 s of
// server each time — 25 of the 63 seconds spent waiting, forty per cent of all
// of it, for a request nobody asked for. Caching could not help either, since
// three minutes is longer than any cache window in the chain.
//
// The fix is not a faster refresh but a better trigger: the alarm is now told
// the instant an event changes (see onEventsChanged below), which is both
// cheaper AND more correct — a reminder set for two minutes' time used to risk
// being missed entirely, because the timer might not come round in time.
// What is left here is only a periodic catch-up for changes made elsewhere:
// another device, or the calendar edited straight in Google.
const REFRESH_MS = 10 * 60 * 1000;

// A snooze that is only a setTimeout dies with the page. Android closes
// backgrounded apps freely, so "remind me in 10 minutes" quietly became "never
// again" — indistinguishable, to the user, from the app forgetting.
const SNOOZE_KEY = 'memo_pending_snooze_v1';

function loadSnooze() {
  try {
    const s = JSON.parse(localStorage.getItem(SNOOZE_KEY) || 'null');
    return s && s.event && Number.isFinite(s.fireAt) ? s : null;
  } catch (_) { return null; }
}
function saveSnooze(s) {
  try {
    if (s) localStorage.setItem(SNOOZE_KEY, JSON.stringify(s));
    else localStorage.removeItem(SNOOZE_KEY);
  } catch (_) {}
}

/**
 * App-wide alarm.
 *
 * Previously the reminder scheduler lived inside TodayView, so alarms only
 * fired while the "Oggi" screen was mounted. This component is mounted once at
 * the app shell level, loads today's events on its own, and therefore rings on
 * ANY screen (Cerca, Note, Impostazioni, …) as long as the app is open.
 */
export default function AlarmManager() {
  const { user } = useAuth();
  const T = useT(user?.language);

  const [events, setEvents] = useState(() => todayEventsCache.get('today') || []);
  const eventsSigRef = useRef('');

  // Alert toast state: { event, stopFn, snoozeCount } | null
  const [alertToast, setAlertToast] = useState(null);
  const alertStopFnRef   = useRef(null); // current active stop function
  const maxAlarmTimerRef = useRef(null); // auto-stop after 5 minutes
  const snoozeTimerRef   = useRef(null); // pending snooze re-fire
  // Reminders that came in while another one was already on screen. They used
  // to be thrown away: two appointments at nine o'clock, and the app told you
  // about one of them.
  const queueRef         = useRef([]);   // [{ event, stopFn }]

  // ── Load events for scheduling (own fetch, independent of the visible screen) ──
  useEffect(() => {
    let cancelled = false;

    // Compact signature of the scheduling-relevant fields. We only replace the
    // events array when it actually changes, so a routine refresh that returns
    // identical data doesn't re-render or re-run the scheduler effect.
    // The title is in here because the alarm SAYS it out loud: without it,
    // renaming an event left the spoken reminder announcing the old name.
    const sigOf = (list) => (list || [])
      .map(e => `${e.id}|${e.start}|${e.title}|${e.alertType}|${e.alertMinutes}|${(e.alertMinutesList || []).join(',')}`)
      .join(';');
    const applyEvents = (list) => {
      const sig = sigOf(list);
      if (sig === eventsSigRef.current) return;
      eventsSigRef.current = sig;
      setEvents(list);
    };

    function loadEvents() {
      // Load a 7-day window (not just today) so long-lead reminders — "1 day"
      // or "1 week before" — fire on time: their alert moment is today even
      // though the event itself is days away.
      const now = new Date();
      const p = n => String(n).padStart(2, '0');
      const startStr = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      apiClient.get('/events/week', { params: { tz, start: startStr } })
        .then((res) => {
          if (cancelled) return;
          const map = res.data || {};
          if (map[startStr]) todayEventsCache.set('today', map[startStr]); // keep TodayView's instant paint fresh
          applyEvents(Object.values(map).flat());
        })
        .catch(() => { /* keep whatever we had; the scheduler just won't gain new events */ });
    }

    // The clock only runs while the app is actually being looked at. In the
    // background nobody is adding events, and each of these costs the server
    // the better part of a second — twenty an hour, competing with the requests
    // the user actually made. Coming back to the app refreshes at once, so
    // nothing is missed by staying quiet meanwhile.
    let id = null;
    const startClock = () => { if (id === null) id = setInterval(loadEvents, REFRESH_MS); };
    const stopClock  = () => { if (id !== null) { clearInterval(id); id = null; } };

    loadEvents();
    if (document.visibilityState === 'visible') startClock();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') { loadEvents(); startClock(); }
      else stopClock();
    };
    const onFocus = () => { loadEvents(); startClock(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    // The real trigger: saving, editing or deleting an event tells us at once.
    // Before this, a reminder set for two minutes' time might not be known to
    // the scheduler until its timer next came round — up to three minutes, i.e.
    // after the moment it was supposed to ring.
    const unsubscribe = onEventsChanged(() => loadEvents());

    return () => {
      cancelled = true;
      stopClock();
      unsubscribe();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Show the next reminder that was waiting, if any. Called after Stop, after
  // Snooze, and when a reminder times out on its own — so a pile-up at nine
  // o'clock is worked through one at a time instead of all but one being lost.
  const showNextQueued = useCallback(function next() {
    const item = queueRef.current.shift();
    if (!item) return false;
    const { event } = item;
    // Its own fresh sound: the original one was stopped when it was queued.
    let stop = () => {};
    if (event.alertType === 'alarm') {
      const { volume, sound } = getAlarmSettings();
      stop = loopAlertSound(sound, volume).stop;
    }
    alertStopFnRef.current = stop;
    setAlertToast({ event, stopFn: stop, snoozeCount: 0 });
    announceReminderA11y(event, { speechLang: langToSpeechLang(user?.language || 'it') });
    maxAlarmTimerRef.current = setTimeout(() => {
      stop();
      alertStopFnRef.current = null;
      setAlertToast(null);
      next();
    }, 5 * 60 * 1000);
    return true;
  }, [user]);

  const handleAlert = useCallback((event, stopFn) => {
    // Something is already ringing: wait your turn instead of shoving it off
    // the screen. The queue is drained by Stop, by Snooze, and by the timeout.
    // Silence this one NOW — the scheduler starts the sound before handing the
    // reminder over, so a queued alarm would otherwise go on ringing underneath
    // the one on screen. Its turn comes with a fresh sound of its own.
    if (alertStopFnRef.current) {
      stopFn();
      queueRef.current.push({ event });
      return;
    }
    clearTimeout(maxAlarmTimerRef.current);
    clearTimeout(snoozeTimerRef.current);

    alertStopFnRef.current = stopFn;
    setAlertToast({ event, stopFn, snoozeCount: 0 });

    // Accessibility (opt-in): vibrate + read the reminder aloud when it fires.
    announceReminderA11y(event, { speechLang: langToSpeechLang(user?.language || 'it') });

    // Auto-stop after 5 minutes to prevent infinite looping if user leaves the app
    maxAlarmTimerRef.current = setTimeout(() => {
      stopFn();
      alertStopFnRef.current = null;
      setAlertToast(null);
      showNextQueued();   // whoever was behind it still deserves to be heard
    }, 5 * 60 * 1000);
  }, [user, showNextQueued]);

  function handleAlertStop() {
    alertStopFnRef.current?.();
    alertStopFnRef.current = null;
    clearTimeout(maxAlarmTimerRef.current);
    clearTimeout(snoozeTimerRef.current);
    saveSnooze(null);
    setAlertToast(null);
    showNextQueued();
  }

  function handleAlertSnooze() {
    if (!alertToast) return;
    const settings = getAlarmSettings();
    if (alertToast.snoozeCount >= settings.maxSnooze) return;

    // Stop current alarm
    alertStopFnRef.current?.();
    alertStopFnRef.current = null;
    clearTimeout(maxAlarmTimerRef.current);

    const { event, snoozeCount } = alertToast;
    const newSnoozeCount = snoozeCount + 1;
    setAlertToast(null); // dismiss current toast while snooze countdown runs

    // Written down, not just held in a timer: if the app is closed — or Android
    // closes it — the snooze is picked up again on the next start.
    const fireAt = Date.now() + settings.snoozeMin * 60 * 1000;
    saveSnooze({ event, fireAt, snoozeCount: newSnoozeCount });

    // Somebody else may have been waiting behind this one.
    showNextQueued();

    snoozeTimerRef.current = setTimeout(() => {
      fireSnoozed(event, newSnoozeCount);
    }, settings.snoozeMin * 60 * 1000);
  }

  // Ring a snoozed reminder — from the timer above, or from the check on
  // startup when the app was closed while the snooze was counting down.
  const fireSnoozed = useCallback((event, snoozeCount) => {
    saveSnooze(null);
    if (alertStopFnRef.current) {          // something else is ringing: queue it
      queueRef.current.push({ event });
      return;
    }
    // A silent "message" reminder must come back silent: snoozing one used to
    // bring it back as a full alarm, which is not what was asked for.
    let stop = () => {};
    if (event.alertType === 'alarm') {
      const { volume, sound } = getAlarmSettings();
      stop = loopAlertSound(sound, volume).stop;
    }
    alertStopFnRef.current = stop;
    announceReminderA11y(event, { speechLang: langToSpeechLang(user?.language || 'it') });
    setAlertToast({ event, stopFn: stop, snoozeCount });

    maxAlarmTimerRef.current = setTimeout(() => {
      stop();
      alertStopFnRef.current = null;
      setAlertToast(null);
      showNextQueued();
    }, 5 * 60 * 1000);
  }, [user, showNextQueued]);

  // On startup: was a snooze left counting down when the app went away?
  // Ring it now if its moment has come, or re-arm the timer if it hasn't.
  useEffect(() => {
    const pending = loadSnooze();
    if (!pending) return undefined;
    const wait = pending.fireAt - Date.now();
    // More than an hour late is no longer a reminder, it is a puzzle.
    if (wait < -60 * 60 * 1000) { saveSnooze(null); return undefined; }
    if (wait <= 0) { fireSnoozed(pending.event, pending.snoozeCount || 1); return undefined; }
    const t = setTimeout(() => fireSnoozed(pending.event, pending.snoozeCount || 1), wait);
    return () => clearTimeout(t);
  }, [fireSnoozed]);

  // Wire up the alert scheduler — checks all loaded events every 30 s
  useAlertScheduler(events, handleAlert);

  // Clean up all alarm timers and active sounds on unmount
  useEffect(() => {
    return () => {
      alertStopFnRef.current?.();
      clearTimeout(maxAlarmTimerRef.current);
      clearTimeout(snoozeTimerRef.current);
    };
  }, []);

  if (!alertToast) return null;

  const settings    = getAlarmSettings();
  const forever     = settings.maxSnooze >= 99;   // "Per sempre"
  const snoozesLeft = settings.maxSnooze - alertToast.snoozeCount;
  const canSnooze   = forever || snoozesLeft > 0;
  const snoozeLabel = T.today.snooze;
  const stopLabel   = T.today.stop;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label={`${T.form?.reminderLabel || 'Reminder'}: ${alertToast.event.title}`}
      style={{
        position: 'fixed', bottom: 88, left: '50%', transform: 'translateX(-50%)',
        zIndex: 200,
        background: 'var(--color-card)',
        border: '2px solid var(--color-primary)',
        borderRadius: 16,
        padding: '12px 14px',
        boxShadow: '0 6px 28px rgba(0,0,0,0.22)',
        maxWidth: 360, width: 'calc(100% - 40px)',
        animation: 'fadeInUp 0.25s ease',
      }}>
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🔔</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary)', marginBottom: 2 }}>
            {T.form?.reminderLabel || 'Reminder'}
            {alertToast.snoozeCount > 0 && (
              <span style={{ marginInlineStart: 6, opacity: 0.7, fontWeight: 500 }}>
                ({snoozeLabel} ×{alertToast.snoozeCount})
              </span>
            )}
          </div>
          {/* Tapping the title opens the event — and stops the ringing on the
              way. The alarm used to be a dead end: it told you the name and
              nothing else, so to see where you had to be, or what to bring,
              you dismissed it and went hunting for the event yourself. */}
          <Link
            to={`/edit/${alertToast.event.id}`}
            state={{ event: alertToast.event }}
            onClick={handleAlertStop}
            style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
          >
            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {iconOf(alertToast.event.title) &&
                <EventIcon icon={iconOf(alertToast.event.title)} size={15} style={{ marginInlineEnd: 5 }} />}
              {stripIcon(alertToast.event.title)}
            </div>
          </Link>
        </div>
      </div>
      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8 }}>
        {canSnooze && (
          <button
            onClick={handleAlertSnooze}
            style={{
              flex: 1, padding: '9px 0', borderRadius: 10,
              background: 'var(--color-surface)',
              border: '1.5px solid var(--color-border)',
              color: 'var(--color-text)', fontWeight: 700, fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            ⏸ {snoozeLabel} {settings.snoozeMin}min
            {!forever && snoozesLeft < settings.maxSnooze && (
              <span style={{ opacity: 0.6, fontWeight: 500, marginInlineStart: 4 }}>({snoozesLeft})</span>
            )}
          </button>
        )}
        <button
          onClick={handleAlertStop}
          style={{
            flex: canSnooze ? 0.6 : 1, padding: '9px 0', borderRadius: 10,
            background: 'var(--color-primary)',
            border: 'none',
            color: '#fff', fontWeight: 700, fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          ■ {stopLabel}
        </button>
      </div>
    </div>
  );
}
